import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Icon } from 'semantic-ui-react';
import { Modal, ModulesFilter, VALIDATION_PATTERNS, USER_PERMISSIONS } from 'dyl-components';

import AddOpportunityModal from "shared/modals/AddOpportunityModal";

import {
    KEY_CREATED, KEY_CREATED_BY, KEY_OWNER_ID, KEY_LAST_MODIFIED,
    KEY_LAST_MODIFIED_BY, KEY_CAMPAIGN_SOURCE, KEY_OPPORTUNITY_ID,
    KEY_OPPORTUNITY_NAME, KEY_CONVERT, KEY_CONTACT_NAME, KEY_ACCOUNT,
    KEY_ACTIONS, KEY_STAGE, KEY_OUTCOME, KEY_LOST_REASON,
    KEY_CO_OWNER,
    KEY_MASTER_SECONDARY
} from 'shared/constants/MODULE_COLUMNS';
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp';
import { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import ContactBulkActionsModal from 'shared/modals/ContactBulkActionsModal';
import opportunitiesActions from 'actions/opportunities';
import { useSearchParams } from 'react-router-dom';
import RestrictedView from 'shared/RestrictedView';

const COLUMN_ROWS = [
    [
        { value: KEY_CREATED, label: "Created" },
        { value: KEY_LOST_REASON, label: "Lost Reason/Type" },
        { value: KEY_CREATED_BY, label: "Created by" },
    ],
    [
        { value: KEY_OWNER_ID, label: "Owned By" },
        { value: KEY_CO_OWNER, label: "Co-Owned By" },
        { value: KEY_LAST_MODIFIED, label: "Last Modified" },
    ],
    [
        { value: KEY_LAST_MODIFIED_BY, label: "Last Modified By" },
        { value: KEY_CAMPAIGN_SOURCE, label: "Campaign Converted" },
        { value: KEY_OPPORTUNITY_ID, label: "Opportunity ID", isForcedChecked: true },
    ],
    [
        { value: KEY_CONTACT_NAME, label: "Contact Name", isForcedChecked: true },
        { value: KEY_OPPORTUNITY_NAME, label: "Opportunity Name", isForcedChecked: true },
        { value: KEY_CONVERT, label: "Convert" }
    ],
    [
        { value: KEY_ACCOUNT, label: "Account" },
        { value: KEY_ACTIONS, label: "Actions" },
        { value: KEY_STAGE, label: "Opportunity Stage" },
    ],
    [
        { value: KEY_OUTCOME, label: "Outcome" },
        { value: KEY_MASTER_SECONDARY, label: "Master/Secondary" },

    ],
];

const OpportunitiesToolbar = ({ search, onSearchSubmit, cancelFunction, onChangeSearch, isColumnHidden, onToggleColumn }) => {
    const { isReadingOpportunities, isReadingDisplay } = useSelector(state => ({
        isReadingOpportunities: state.opportunities.isReadingOpportunities
    }));

    const [isAddOpportunityModalOpen, setIsAddOpporunityModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [selectedOpportunities, , areOpportunitiesInAllPagesSelected] = useContext(BulkActionsContext);
    const [isBulkActionsModalOpen, setIsBulkActionsModalOpen] = useState(false);

    const { count } = useSelector(state => state.opportunities);

    const onOpenAddOpportunityModal = () => {
        setIsAddOpporunityModalOpen(true);
    }

    const onCloseAddOpportunityModal = () => {
        setIsAddOpporunityModalOpen(false);
    }

    const hasSelectedOpportunities = selectedOpportunities.length || areOpportunitiesInAllPagesSelected;
    const numberOfSelectedOpportunities = areOpportunitiesInAllPagesSelected ? count : selectedOpportunities.length;

    const onOpenBulkActionsModal = () => {
        setIsBulkActionsModalOpen(true);
    }
    const onCloseBulkActionsModal = () => {
        setIsBulkActionsModalOpen(false);
    }
    const dispatch = useDispatch();
    const [params] = useSearchParams();
    return (
        <React.Fragment>
            <Grid>
                <Grid.Row columns='equal' verticalAlign='middle'>
                    <Grid.Column width={5}>
                        <ModulesFilter
                            isLoading={isReadingOpportunities || isReadingDisplay}
                            search={search}
                            onSearchSubmit={onSearchSubmit}
                            cancelFunction={cancelFunction}
                            onChangeSearch={onChangeSearch}
                            columnRows={COLUMN_ROWS}
                            columnCount={3}
                            isColumnHidden={isColumnHidden}
                            onToggleColumn={onToggleColumn}
                            validationPattern={VALIDATION_PATTERNS.alphanumeric_w_special_chars_for_universal_search} 
                            searchBarPlaceholder={"Search by contact, opportunity name, email or phone number"}                           
                        />
                    </Grid.Column>

                    <Grid.Column width={6} floated='right'>
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_ADD}>
                            <Button floated='right' className='ModulesListToolbar__NewModulesListButton' onClick={onOpenAddOpportunityModal}>
                                <Icon className='plus' /> New Opportunity
                            </Button>
                        </RestrictedView>
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_ADD}>
                            <Button floated='right' className='ModulesListToolbar__BulkActionButton' disabled={!hasSelectedOpportunities} onClick={onOpenBulkActionsModal}>
                                Bulk Action
                            </Button>
                        </RestrictedView>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Modal open={isAddOpportunityModalOpen} onClose={() => {
                isDirty
                    ? setIsConfirmationOpen(true)
                    : onCloseAddOpportunityModal();
            }}>
                <AddOpportunityModal onOpen={() => { return Promise.resolve(); }} onClose={onCloseAddOpportunityModal} onDirtyChange={(dirty) => setIsDirty(dirty)} />
            </Modal>
            <FullScreenModalPopUp
                header={"Changes not saved"}
                subheader={"Are you sure you want to exit?"}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    onCloseAddOpportunityModal();
                    setIsConfirmationOpen(false);
                    setIsDirty(false)
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
            <ContactBulkActionsModal
                open={isBulkActionsModalOpen}
                onClose={onCloseBulkActionsModal}
                numberSelectedContacts={numberOfSelectedOpportunities}
                module="opportunity"
                refresh={() => {
                    let columns = (params.get('columns')?.split(',') || [
                        KEY_CREATED,
                        KEY_OPPORTUNITY_ID,
                        KEY_ACCOUNT,
                        KEY_OUTCOME,
                        KEY_CONVERT,
                        KEY_ACTIONS
                    ]).filter(
                        column => ![KEY_CONVERT, KEY_ACTIONS, KEY_CONTACT_NAME].includes(column)
                    );

                    dispatch(opportunitiesActions.readOpportunities({
                        ...Object.fromEntries(params),
                        columns: columns.toString(),
                        page: 1,
                        limit: 25,
                    }));
                }}
            />
        </React.Fragment>
    );
}

export default OpportunitiesToolbar;
